import React, { memo, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { FaChevronDown, FaChevronRight, FaSearch, FaTimes } from 'react-icons/fa';
import { richWordDiff, toPlainText } from 'helpers/richTextDiff';
import DiffWords, { StruckText } from './DiffWords';

const KIND_BADGE = {
  added:   'tw-bg-emerald-50 tw-text-emerald-700 tw-border-emerald-200',
  removed: 'tw-bg-rose-50 tw-text-rose-700 tw-border-rose-200',
  changed: 'tw-bg-amber-50 tw-text-amber-700 tw-border-amber-200',
};

const booleanLabel = (text) => (text === 'true' ? 'Yes' : text === 'false' ? 'No' : null);

const Empty = () => <span className="tw-text-slate-300 tw-italic">empty</span>;

// A whole value: booleans as Yes/No, rich text as plain words with any
// strike-through kept.
const WholeValue = ({ value, className }) => {
  const text = toPlainText(value);
  if (!text) return <Empty />;
  if (booleanLabel(text)) return <span className={className}>{booleanLabel(text)}</span>;
  return <StruckText value={value} className={className} />;
};
WholeValue.propTypes = { value: PropTypes.any, className: PropTypes.string };

// old → new value. For a "changed" field with text on both sides, only the words
// that actually differ are marked (removed in old / added in new; a word struck
// through in this version counts as changed); the rest reads dimmed — so a
// one-word edit in a long note no longer strikes the whole note. Memoized: the
// rich-text diff is not free and the list re-renders on every search keystroke.
const DiffValue = memo(({ oldVal, newVal, kind }) => {
  const parts = kind === 'changed' ? richWordDiff(oldVal, newVal) : null;
  const isBoolean = booleanLabel(toPlainText(oldVal)) || booleanLabel(toPlainText(newVal));

  if (parts && parts.oldParts.length > 0 && parts.newParts.length > 0 && !isBoolean) {
    return (
      <>
        <DiffWords parts={parts.oldParts} tone="old" />
        <span className="tw-text-slate-300 tw-mx-1">→</span>
        <DiffWords parts={parts.newParts} tone="new" />
      </>
    );
  }

  return (
    <>
      <WholeValue value={oldVal} className="tw-text-rose-600 tw-line-through tw-break-words" />
      <span className="tw-text-slate-300 tw-mx-1">→</span>
      <WholeValue value={newVal} className="tw-text-emerald-700 tw-font-medium tw-break-words" />
    </>
  );
});
DiffValue.displayName = 'DiffValue';
DiffValue.propTypes = { oldVal: PropTypes.any, newVal: PropTypes.any, kind: PropTypes.string };

// A summary pill that doubles as a kind filter.
const FilterPill = ({ active, onClick, className, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded-xl tw-text-xs tw-font-semibold tw-border tw-cursor-pointer tw-transition-all ${className} ${active ? 'tw-ring-2 tw-ring-offset-1 tw-ring-slate-300' : 'hover:tw-brightness-95'}`}
  >
    {children}
  </button>
);
FilterPill.propTypes = {
  active: PropTypes.bool,
  onClick: PropTypes.func,
  className: PropTypes.string,
  children: PropTypes.node,
};

const PIFChanges = () => {
  const { diffData, diffLoading } = useSelector((s) => s.crm_project_pif);
  const [kind, setKind] = useState('all');
  const [q, setQ] = useState('');
  const [collapsed, setCollapsed] = useState({});

  const { base, compare, summary = {}, changes = [] } = diffData || {};

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return changes.filter(
      (c) => (kind === 'all' || c.kind === kind) && (!term || (c.label || '').toLowerCase().includes(term))
    );
  }, [changes, kind, q]);

  const groups = useMemo(() => {
    const g = {};
    filtered.forEach((c) => {
      const key = c.area_no ? `${c.section} · Area ${c.area_no}` : c.section;
      (g[key] = g[key] || []).push(c);
    });
    return g;
  }, [filtered]);

  const groupKeys = Object.keys(groups);
  const toggle = (k) => setCollapsed((p) => ({ ...p, [k]: !p[k] }));
  const setAll = (val) => setCollapsed(Object.fromEntries(groupKeys.map((k) => [k, val])));
  const allCollapsed = groupKeys.length > 0 && groupKeys.every((k) => collapsed[k]);

  if (diffLoading) return <div className="tw-p-6 tw-text-slate-500">Comparing…</div>;
  if (!diffData) return <div className="tw-p-6 tw-text-slate-500">Select a revision / change order to compare with.</div>;

  if (!changes.length) {
    return (
      <div className="tw-p-8 tw-text-center tw-text-slate-400">
        No changes between <b className="tw-text-slate-600">{base?.label}</b> and <b className="tw-text-slate-600">{compare?.label}</b>.
      </div>
    );
  }

  return (
    <div className="tw-p-3 tw-space-y-3">
      {/* ── Toolbar: summary filters + search ── */}
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
        <span className="tw-font-semibold tw-text-slate-700 tw-text-sm tw-mr-1">
          {base?.label} <span className="tw-text-slate-300">→</span> {compare?.label}
        </span>
        <FilterPill active={kind === 'all'} onClick={() => setKind('all')} className="tw-bg-slate-100 tw-text-slate-600 tw-border-slate-200">
          {summary.total} all
        </FilterPill>
        {summary.added > 0 && (
          <FilterPill active={kind === 'added'} onClick={() => setKind(kind === 'added' ? 'all' : 'added')} className="tw-bg-emerald-50 tw-text-emerald-700 tw-border-emerald-200">+{summary.added} added</FilterPill>
        )}
        {summary.removed > 0 && (
          <FilterPill active={kind === 'removed'} onClick={() => setKind(kind === 'removed' ? 'all' : 'removed')} className="tw-bg-rose-50 tw-text-rose-700 tw-border-rose-200">−{summary.removed} removed</FilterPill>
        )}
        {summary.changed > 0 && (
          <FilterPill active={kind === 'changed'} onClick={() => setKind(kind === 'changed' ? 'all' : 'changed')} className="tw-bg-amber-50 tw-text-amber-700 tw-border-amber-200">~{summary.changed} changed</FilterPill>
        )}

        <div className="tw-relative tw-ml-auto">
          <FaSearch className="tw-absolute tw-left-2.5 tw-top-1/2 tw--translate-y-1/2 tw-text-slate-400" size={11} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search field…"
            className="tw-w-48 tw-h-8 tw-pl-7 tw-pr-6 tw-text-sm tw-border tw-border-slate-300 tw-rounded tw-bg-white focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary-400 focus:tw-border-transparent tw-text-slate-700 tw-placeholder-slate-400"
          />
          {q && (
            <button type="button" onClick={() => setQ('')} className="tw-absolute tw-right-2 tw-top-1/2 tw--translate-y-1/2 tw-text-slate-400 hover:tw-text-slate-600 tw-border-0 tw-bg-transparent tw-cursor-pointer tw-p-0">
              <FaTimes size={11} />
            </button>
          )}
        </div>
        <button type="button" onClick={() => setAll(!allCollapsed)} className="tw-h-8 tw-px-3 tw-text-sm tw-font-medium tw-border tw-border-slate-300 tw-rounded tw-bg-white tw-text-slate-600 hover:tw-bg-slate-50 tw-cursor-pointer">
          {allCollapsed ? 'Expand all' : 'Collapse all'}
        </button>
      </div>

      {/* ── Grouped changes ── */}
      {groupKeys.length === 0 ? (
        <div className="tw-p-6 tw-text-center tw-text-slate-400">No changes match the filter.</div>
      ) : (
        groupKeys.map((grp) => {
          const rows = groups[grp];
          const isCollapsed = collapsed[grp];
          const a = rows.filter((r) => r.kind === 'added').length;
          const r = rows.filter((x) => x.kind === 'removed').length;
          const ch = rows.filter((x) => x.kind === 'changed').length;
          return (
            <div key={grp} className="tw-rounded tw-border tw-border-slate-200 tw-overflow-hidden tw-bg-white">
              <button
                type="button"
                onClick={() => toggle(grp)}
                className="tw-w-full tw-flex tw-items-center tw-justify-between tw-px-3 tw-py-2 tw-bg-slate-50 hover:tw-bg-slate-100 tw-border-0 tw-cursor-pointer tw-transition-colors"
              >
                <span className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wide">
                  {isCollapsed ? <FaChevronRight size={10} /> : <FaChevronDown size={10} />}
                  {grp}
                  <span className="tw-text-slate-400 tw-font-medium tw-normal-case">({rows.length})</span>
                </span>
                <span className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-semibold">
                  {a > 0 && <span className="tw-text-emerald-600">+{a}</span>}
                  {r > 0 && <span className="tw-text-rose-600">−{r}</span>}
                  {ch > 0 && <span className="tw-text-amber-600">~{ch}</span>}
                </span>
              </button>
              {!isCollapsed && (
                <div className="tw-divide-y tw-divide-slate-100">
                  {rows.map((c, i) => {
                    return (
                      <div key={`${c.path}-${i}`} className="tw-px-3 tw-py-2.5 hover:tw-bg-slate-50">
                        <div className="tw-flex tw-items-center tw-gap-2 tw-mb-1">
                          <span className={`tw-inline-flex tw-items-center tw-px-1.5 tw-py-0.5 tw-rounded-sm tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-wide tw-border ${KIND_BADGE[c.kind] || KIND_BADGE.changed}`}>
                            {c.kind}
                          </span>
                          <span className="tw-font-medium tw-text-slate-800">{c.label}</span>
                        </div>
                        <div className="tw-flex tw-items-baseline tw-gap-1 tw-flex-wrap tw-pl-0.5 tw-text-sm">
                          <DiffValue oldVal={c.old} newVal={c.new} kind={c.kind} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

export default PIFChanges;
